import { useCallback, useMemo } from 'react'

import { useEditor } from '@craftjs/core'
import { Save, LoaderCircle } from 'lucide-react'
import { Button, InputGroup, InputGroupText } from 'twyne-new.ui'

import { useAppAlert } from '@/components/AppAlert'
import { useSerializeWithoutRemapping } from '@/components/builder/utils/serialize-block'
import { useSnapshotBlock } from '@/components/builder/utils/snapshot-block'
import { useBuilderStore } from '@/components/builder/utils/store'
import { PageButton, LayersButton, AssetsButton, BlocksButton } from '@/modules/cms/views/post/components/builder/Content/Toolbar'

import { getSerializedRootNodeId } from './utils'

export default function Header({ page = <PageButton />, layers = <LayersButton />, assets = <AssetsButton />, blocks = <BlocksButton /> }) {
  return <>
    <div className='p-4 twy-bg-base-accent flex justify-between items-center'>
      <div className='flex gap-4'>
        {page}
        {assets}
        {layers}
        {blocks}
      </div>
      <div className='flex gap-2'>
        <Actions />
      </div>
    </div>
  </>
}

function Actions() {
  const { query } = useEditor()
  const nodes = query.getSerializedNodes()
  const nodeId = useMemo(() => getSerializedRootNodeId(nodes), [nodes])

  return <>
    <Action rootNodeId={nodeId} />
  </>
}

function Action({ rootNodeId }) {
  const { loading, saveBlock, uploadBlockImage, postBuilder: { item } } = useBuilderStore()
  const { handleSuccess, handleError } = useAppAlert()

  const handleSnapshot = useSnapshotBlock(rootNodeId)
  const handleSerialize = useSerializeWithoutRemapping(rootNodeId)

  const handleUpload = useCallback(async file => {
    if (!file) {
      return null
    }

    const data = new FormData()
    data.append('file', file, 'preview.png')
    const response = await uploadBlockImage(data)

    return {
      key: response.key,
      url: response.location,
    }
  }, [uploadBlockImage])

  const handleSave = useCallback(async () => {
    try {
      const definition = handleSerialize()
      const image = await handleSnapshot()
      const file = await handleUpload(image)

      await saveBlock({
        ...item,
        definition,
        newFile: file,
      })

      handleSuccess('Success', "Block saved.")
    } catch (error) {
      handleError('Error', "Block was not saved.")
    }
  }, [item, saveBlock, handleSerialize, handleSuccess, handleError, handleSnapshot, handleUpload])

  return <>
    <InputGroup button>
      <InputGroupText addonType='prepend'>
        <Button className="items-center" color='accent' variant='outline' onClick={handleSave} disabled={!!loading}>
          {loading ? <LoaderCircle className="animate-spin" size={16} /> : <Save size={16} />}
          Save
        </Button>
      </InputGroupText>
    </InputGroup>
  </>
}
